import { useEffect, useState } from "react";
import { useMe } from "../api/use-me.js";

// Vaihe UM.DL — Kratti.app:n lataus-näkymä.
//
// Public-näkymä (kaikki kirjautuneet): suuri "Lataa uusin versio"-painike,
// johdantoteksti, asennusohjeet.
//
// Admin-näkymä: lisäksi aiemmat versiot + jakelulinkit.

type VersionEntry = {
  version: string;
  filename: string;
  size: number;
  url: string;
  released: string;
  /** Mini-markdown: **bold**, listat alkavat "-", rivit \n */
  notes?: string;
};

type VersionsJson = {
  latest: string;
  versions: VersionEntry[];
};

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Minimal markdown: render \n-erotetut rivit. **text** → <strong>.
 * Listat: rivi alkaa "- " → ul/li. Muu → paragraph.
 */
function NotesRenderer({ source }: { source: string }) {
  const lines = source.split("\n");
  type Block =
    | { type: "p"; text: string }
    | { type: "ul"; items: string[] };
  const blocks: Block[] = [];
  let currentList: string[] | null = null;
  for (const raw of lines) {
    const line = raw.trim();
    if (line.startsWith("- ")) {
      if (!currentList) {
        currentList = [];
        blocks.push({ type: "ul", items: currentList });
      }
      currentList.push(line.slice(2));
    } else if (line === "") {
      currentList = null;
    } else {
      currentList = null;
      blocks.push({ type: "p", text: line });
    }
  }

  function renderInline(text: string): React.ReactNode[] {
    // **bold** → <strong>
    const out: React.ReactNode[] = [];
    let i = 0;
    let key = 0;
    while (i < text.length) {
      const start = text.indexOf("**", i);
      if (start === -1) {
        out.push(text.slice(i));
        break;
      }
      const end = text.indexOf("**", start + 2);
      if (end === -1) {
        out.push(text.slice(i));
        break;
      }
      if (start > i) out.push(text.slice(i, start));
      out.push(<strong key={key++}>{text.slice(start + 2, end)}</strong>);
      i = end + 2;
    }
    return out;
  }

  return (
    <div className="space-y-2 text-sm text-slate-300">
      {blocks.map((b, i) =>
        b.type === "p" ? (
          <p key={i}>{renderInline(b.text)}</p>
        ) : (
          <ul key={i} className="list-disc space-y-1 pl-5">
            {b.items.map((item, j) => (
              <li key={j}>{renderInline(item)}</li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("fi-FI", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function Lataa() {
  const { me } = useMe();
  const isAdmin = me?.user.roles.includes("admin") ?? false;

  const [data, setData] = useState<VersionsJson | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    fetch("/downloads/versions.json", { cache: "no-cache" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<VersionsJson>;
      })
      .then(setData)
      .catch((err) => setError(err.message));
  }, []);

  async function copyLink(url: string) {
    try {
      const full = `${window.location.origin}${url}`;
      await navigator.clipboard.writeText(full);
      setCopied(url);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // ignore
    }
  }

  if (error) {
    return (
      <section className="mx-auto max-w-3xl px-6 py-10">
        <h2 className="text-2xl font-semibold text-slate-100">Lataa Kratti</h2>
        <p className="mt-4 text-red-300">Versiotietojen lataus epäonnistui: {error}</p>
      </section>
    );
  }

  if (!data) {
    return (
      <section className="mx-auto max-w-3xl px-6 py-10">
        <p className="text-slate-500">Ladataan…</p>
      </section>
    );
  }

  const latest = data.versions.find((v) => v.version === data.latest);

  return (
    <section className="mx-auto max-w-3xl space-y-8 px-6 py-10">
      <header>
        <h2 className="text-2xl font-semibold tracking-tight text-slate-100">
          Lataa Kratti macOS:lle
        </h2>
        <p className="mt-1 text-sm text-slate-400">
          Asenna Kratti omalle Macillesi. Asennus on yksinkertainen — vedä
          Kratti.app /Applications-kansioon ja avaa.
        </p>
      </header>

      {/* Uusin versio — keskeinen lataus-painike */}
      {latest && (
        <section className="rounded-lg border border-cyan-700 bg-cyan-950/30 p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-cyan-100">
                Versio {latest.version}
              </h3>
              <p className="mt-1 text-sm text-slate-400">
                Julkaistu {formatDate(latest.released)} · {formatSize(latest.size)}
              </p>
            </div>
            <a
              href={latest.url}
              className="rounded-md bg-cyan-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-cyan-500"
            >
              Lataa Kratti
            </a>
          </div>

          {latest.notes && (
            <details className="mt-4" open>
              <summary className="cursor-pointer text-sm font-medium text-cyan-300 hover:underline">
                Mikä uutta versiossa {latest.version}
              </summary>
              <div className="mt-3 rounded border border-cyan-900/50 bg-slate-950/60 p-3">
                <NotesRenderer source={latest.notes} />
              </div>
            </details>
          )}

          <details className="mt-3">
            <summary className="cursor-pointer text-sm text-cyan-300 hover:underline">
              Asennusohjeet
            </summary>
            <ol className="mt-3 list-decimal space-y-1.5 pl-6 text-sm text-slate-300">
              <li>Kaksoisklikkaa ladattua <code className="rounded bg-slate-800 px-1">kratti-{latest.version}.dmg</code>-tiedostoa</li>
              <li>Vedä Kratti.app näkyvään Applications-symboliin</li>
              <li>Avaa Applications → Kratti.app</li>
              <li>macOS näyttää "Open"-prompt:n → klikkaa Open</li>
              <li>Menubar:iin ilmestyy Kratti-ikoni — first-run-wizard avautuu</li>
              <li>Anna email + salasana, daemon asentuu automaattisesti</li>
            </ol>
          </details>
        </section>
      )}

      {/* Admin: aiemmat versiot + muutokset */}
      {isAdmin && data.versions.length > 1 && (
        <section className="rounded-lg border border-slate-700 bg-slate-900 p-6">
          <h3 className="text-lg font-medium text-slate-100">Aiemmat versiot</h3>
          <p className="mt-1 text-xs text-slate-500">
            Vain admin-näkymä. Voit kopioida jakelulinkin "Kopioi"-painikkeella.
          </p>

          <ul className="mt-4 space-y-3">
            {data.versions.slice(1).map((v) => (
              <li
                key={v.version}
                className="rounded border border-slate-800 bg-slate-950/40 p-4"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div>
                    <span className="text-base font-medium text-slate-100">
                      {v.version}
                    </span>
                    <span className="ml-3 text-xs text-slate-500">
                      {formatDate(v.released)} · {formatSize(v.size)}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <a
                      href={v.url}
                      className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-200 hover:bg-slate-800"
                    >
                      Lataa
                    </a>
                    <button
                      type="button"
                      onClick={() => copyLink(v.url)}
                      className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-200 hover:bg-slate-800"
                    >
                      {copied === v.url ? "Kopioitu ✓" : "Kopioi linkki"}
                    </button>
                  </div>
                </div>
                {v.notes && (
                  <details className="mt-3">
                    <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-200">
                      Muutokset
                    </summary>
                    <div className="mt-2 rounded border border-slate-800 bg-slate-950 p-3">
                      <NotesRenderer source={v.notes} />
                    </div>
                  </details>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </section>
  );
}
